import * as React from "react";
import { useNavigate } from "react-router-dom";
import { CheckmarkRegular, ChevronUpRegular } from "@fluentui/react-icons";
import { useTestingAgentClient } from "../api/TestingAgentApiClient";
import type { ProjectListItem } from "../api/models/ApiModels";
import { useProjectSelectorStyles } from "../styles/projectSelector.styles";

const projectCache = new Map<string, ProjectListItem[]>();

interface ProjectSelectorProps {
    resourceId: string;
    currentProjectId: string;
    currentProjectName: string;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
    resourceId,
    currentProjectId,
    currentProjectName,
}) => {
    const s = useProjectSelectorStyles();
    const navigate = useNavigate();
    const { getProjects } = useTestingAgentClient();

    const [open, setOpen] = React.useState(false);
    const [projects, setProjects] = React.useState<ProjectListItem[]>(
        () => projectCache.get(resourceId) ?? [],
    );
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const cached = projectCache.get(resourceId);
        setProjects(cached ?? []);
        setError(null);
        setLoading(false);
    }, [resourceId]);

    React.useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    React.useEffect(() => {
        if (!open) return;
        const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, [open]);

    const handleOpen = async () => {
        const nextOpen = !open;
        setOpen(nextOpen);
        if (nextOpen && !projectCache.has(resourceId)) {
            setLoading(true);
            setError(null);
            try {
                const result = await getProjects(resourceId);
                const items = result.value ?? [];
                projectCache.set(resourceId, items);
                setProjects(items);
            } catch (e) {
                setError(e instanceof Error ? e.message : "Failed to load projects");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleSelectProject = (project: ProjectListItem) => {
        if (!project.projectId || project.projectId === currentProjectId) { setOpen(false); return; }
        setOpen(false);
        navigate(`/dashboard/${encodeURIComponent(resourceId)}/${encodeURIComponent(project.projectId)}`);
    };

    return (
        <div ref={wrapperRef} className={s.wrapper}>
            <button
                className={s.trigger}
                onClick={() => void handleOpen()}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                {currentProjectName}
                <span className={s.triggerCaret}><ChevronUpRegular style={{ fontSize: 12 }} /></span>
            </button>

            {open && (
                <div className={s.dropdown} role="listbox">
                    <div className={s.sectionHeader}>Recent projects</div>
                    {loading && <div className={s.loading}>Loading…</div>}
                    {error && <div className={s.error}>{error}</div>}
                    {!loading && !error && (
                        <ul className={s.list}>
                            {projects.map((p, i) => {
                                const isSelected = p.projectId === currentProjectId;
                                return (
                                    <li
                                        key={p.projectId ?? p.name ?? i}
                                        className={isSelected ? s.itemSelected : s.item}
                                        role="option"
                                        aria-selected={isSelected}
                                        tabIndex={0}
                                        onClick={() => handleSelectProject(p)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                handleSelectProject(p);
                                            }
                                        }}
                                    >
                                        {isSelected ? <span className={s.checkIcon}><CheckmarkRegular style={{ fontSize: 14 }} /></span> : <span className={s.checkPlaceholder} />}
                                        <span className={s.itemName}>{p.name ?? p.projectId ?? "—"}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <div className={s.divider} />
                    <button className={s.linkItem} onClick={() => { setOpen(false); navigate(`/?resourceId=${encodeURIComponent(resourceId)}`); }}>
                        View all projects
                    </button>
                </div>
            )}
        </div>
    );
};
